/* YOUNI.ONE shared video system
 * - Poster first
 * - Hero videos hydrate immediately on the active device
 * - Lazy videos hydrate only when near the viewport
 * - Autoplay only while visible
 * - Pause when off-screen
 */
(function () {
  'use strict';

  const SELECTOR = '[data-youni-video]';
  const MOBILE_QUERY = window.matchMedia('(max-width: 749px)');
  const REDUCED_MOTION_QUERY = window.matchMedia('(prefers-reduced-motion: reduce)');

  class YouniVideo {
    constructor(wrapper) {
      if (!wrapper || wrapper.dataset.youniVideoReady === 'true') return;

      this.wrapper = wrapper;
      this.video = wrapper.querySelector('.youni-video__video');
      this.mode = wrapper.dataset.mode || 'lazy';
      this.device = wrapper.dataset.device || 'all';
      this.autoplay = wrapper.dataset.autoplay !== 'false';
      this.hydrated = false;
      this.nearObserver = null;
      this.visibilityObserver = null;

      if (!this.video) return;

      this.wrapper.dataset.youniVideoReady = 'true';
      this.bindVideoEvents();
      this.setup();

      this.handleMediaChange = this.handleMediaChange.bind(this);

      if (MOBILE_QUERY.addEventListener) {
        MOBILE_QUERY.addEventListener('change', this.handleMediaChange);
      } else if (MOBILE_QUERY.addListener) {
        MOBILE_QUERY.addListener(this.handleMediaChange);
      }
    }

    isActiveDevice() {
      if (this.device === 'mobile') return MOBILE_QUERY.matches;
      if (this.device === 'desktop') return !MOBILE_QUERY.matches;
      return true;
    }

    bindVideoEvents() {
      this.video.addEventListener('playing', () => {
        this.wrapper.classList.add('has-played');
        this.wrapper.classList.remove('has-error');
      });

      this.video.addEventListener('error', () => {
        this.wrapper.classList.add('has-error');
      });
    }

    hydrate() {
      if (this.hydrated || !this.isActiveDevice()) return;

      const sources = this.video.querySelectorAll('source[data-src]');
      if (!sources.length) return;

      sources.forEach((source) => {
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
      });

      this.hydrated = true;
      this.video.load();
    }

    tryPlay() {
      if (!this.autoplay || !this.hydrated || !this.isActiveDevice()) return;
      if (REDUCED_MOTION_QUERY.matches) return;

      const promise = this.video.play();

      if (promise && typeof promise.catch === 'function') {
        promise.catch(() => {
          /* Keep poster visible if browser blocks autoplay. */
        });
      }
    }

    pause() {
      if (!this.video.paused) {
        this.video.pause();
      }
    }

    setupVisibilityObserver() {
      if (this.visibilityObserver) {
        this.visibilityObserver.disconnect();
      }

      this.visibilityObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!this.isActiveDevice()) {
              this.pause();
              return;
            }

            if (entry.isIntersecting && entry.intersectionRatio >= 0.15) {
              this.hydrate();
              this.tryPlay();
            } else {
              this.pause();
            }
          });
        },
        {
          threshold: [0, 0.15, 0.5]
        }
      );

      this.visibilityObserver.observe(this.wrapper);
    }

    setupLazyObserver() {
      if (this.nearObserver) {
        this.nearObserver.disconnect();
      }

      this.nearObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && this.isActiveDevice()) {
              this.hydrate();
              observer.unobserve(this.wrapper);
            }
          });
        },
        {
          rootMargin: '100% 0px 100% 0px',
          threshold: 0
        }
      );

      this.nearObserver.observe(this.wrapper);
    }

    setup() {
      if (!this.isActiveDevice()) {
        this.pause();
      }

      if (this.mode === 'hero') {
        if (this.isActiveDevice()) {
          this.hydrate();
        }
      } else {
        this.setupLazyObserver();
      }

      this.setupVisibilityObserver();
    }

    handleMediaChange() {
      if (!this.isActiveDevice()) {
        this.pause();
        return;
      }

      if (this.mode === 'hero') {
        this.hydrate();
      } else if (!this.hydrated) {
        this.setupLazyObserver();
      }
    }
  }

  function initYouniVideos(root) {
    const scope = root || document;

    if (scope.matches && scope.matches(SELECTOR)) {
      new YouniVideo(scope);
    }

    scope.querySelectorAll(SELECTOR).forEach((wrapper) => {
      new YouniVideo(wrapper);
    });
  }

  function boot() {
    initYouniVideos(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initYouniVideos(event.target);
  });
})();